import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

import { useBpV2State } from "../../../context/BusinessPlanV2Context";
import { GlobalState } from "../../../context/GlobalContext";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { KeyboardBackspaceRounded } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { grey } from "@mui/material/colors";

import { API, storeGridColumns, STORE_APPROVAL_STATUSES, STORE_APPROVAL_ROLE_OPTIONS } from "../data";
import { bpHomeGridSx } from "../StoreBp/storeBpTheme";
import { withHomeCellLayout } from "../StoreBp/gridCells";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import "../businessPlanV2.css";

const PAGE_SIZE = 10;

const statusChipColor = {
	PENDING: "default",
	"IN PROGRESS": "secondary",
	SUBMITTED: "warning",
	"RM APPROVED": "info",
	"NSM APPROVED": "primary",
	"FINAL APPROVED": "success",
};

const roleChipColor = {
	BP_NSM: "primary",
	BP_ASM: "default",
	BP_RM: "secondary",
	NSM: "primary",
	RSM: "secondary",
	ASM: "default",
	ADMIN: "error",
	BP_FINAL_APPROVER: "success",
};

const StoresGrid = ({ plan, onBack, onStoreClick, onManageSubmissions, refreshKey = 0, hideHeader = false }) => {
	const { setSelectedStore } = useBpV2State();
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();

	const [statusFilter, setStatusFilter] = useState(null);
	const [approvalRoleFilter, setApprovalRoleFilter] = useState([]);
	const [searchInput, setSearchInput] = useState("");
	const [storeSearch, setStoreSearch] = useState("");
	const [stores, setStores] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: PAGE_SIZE });

	useEffect(() => {
		const timer = setTimeout(() => {
			setStoreSearch(searchInput.trim());
			setPaginationModel((prev) => ({ ...prev, page: 0 }));
		}, 350);
		return () => clearTimeout(timer);
	}, [searchInput]);

	const fetchStores = useCallback(async () => {
		if (!plan?.id) return;
		try {
			setLoading(true);
			const { data } = await axios.get(API.GET_PLAN_STORES(plan.id), {
				params: {
					page: paginationModel.page,
					pageSize: paginationModel.pageSize,
					...(statusFilter ? { status: statusFilter } : {}),
					...(approvalRoleFilter.length ? { approvals: approvalRoleFilter.join(",") } : {}),
					...(storeSearch ? { search: storeSearch } : {}),
				},
			});
			if (data.status === "success") {
				setStores(data.result || []);
				setRowCount(Number(data.total) || 0);
			}
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrMsgFromError(err),
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [plan?.id, paginationModel.page, paginationModel.pageSize, statusFilter, approvalRoleFilter, storeSearch, refreshKey]);

	useEffect(() => {
		fetchStores();
	}, [fetchStores]);

	const handleStatusFilter = (status) => {
		const next = statusFilter === status ? null : status;
		setStatusFilter(next);
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
	};

	const handleApprovalRoleFilter = (event) => {
		const value = event.target.value;
		setApprovalRoleFilter(typeof value === "string" ? value.split(",") : value);
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
	};

	const handleRowClick = (params) => {
		setSelectedStore(params.row);
		onStoreClick?.(params.row);
	};

	const getRowClassName = (params) => {
		const store = params.row;
		if (store.removed) return "removed-row";
		const finalSubmission = store.submissions?.find((s) => s.is_final && s.status === "APPROVED");
		if (finalSubmission?.completed) return "green";
		if (!store.submissions?.length) return "yellow";
		return "";
	};

	const columns = useMemo(
		() => [
			...withHomeCellLayout(storeGridColumns).map((col) => {
				if (col.field === "store_name") {
					return {
						...col,
						renderCell: (params) => (
							<Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>
								<Typography
									fontSize={13.5}
									fontWeight={600}
									color={params.row.removed ? grey[500] : grey[900]}
									noWrap
									sx={{ textDecoration: params.row.removed ? "line-through" : "none" }}
								>
									{params.value || "—"}
								</Typography>
								{params.row.removed && (
									<Tooltip title="No longer present in BigQuery store master — history is preserved">
										<Chip
											label="Removed"
											size="small"
											color="error"
											variant="outlined"
											sx={{ height: 18, fontSize: 9.5, fontWeight: 700, borderRadius: 1 }}
										/>
									</Tooltip>
								)}
							</Stack>
						),
					};
				}
				if (col.field === "approval_status") {
					return {
						...col,
						renderCell: (params) => (
							<Chip
								label={params.value}
								size="small"
								color={statusChipColor[params.value] || "default"}
								variant={
									params.value === "IN PROGRESS" ||
									params.value === "SUBMITTED" ||
									params.value === "RM APPROVED" ||
									params.value === "NSM APPROVED" ||
									params.value === "FINAL APPROVED"
										? "filled"
										: "outlined"
								}
								sx={{
									height: 24,
									fontSize: 11,
									fontWeight: 600,
									borderRadius: 1.25,
								}}
							/>
						),
					};
				}
				if (col.field === "approval_roles") {
					return {
						...col,
						renderCell: (params) => {
							const roles = params.row.approval_roles || [];
							if (!roles.length) {
								return (
									<Typography variant="caption" color="text.secondary">
										—
									</Typography>
								);
							}
							return (
								<Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
									{roles.map((role) => (
										<Chip
											key={role}
											label={role}
											size="small"
											color={roleChipColor[role] || "default"}
											variant="outlined"
											sx={{ height: 22, fontSize: 10, fontWeight: 600, borderRadius: 1 }}
										/>
									))}
								</Stack>
							);
						},
					};
				}
				return col;
			}),
			{
				field: "actions",
				headerName: "",
				width: 48,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				align: "center",
				headerAlign: "center",
				renderCell: (params) => (
					<Tooltip title="Manage submissions">
						<IconButton
							size="small"
							sx={{
								color: grey[500],
								"&:hover": { color: grey[800], bgcolor: grey[100] },
							}}
							onClick={(e) => {
								e.stopPropagation();
								setSelectedStore(params.row);
								onManageSubmissions?.(params.row);
							}}
						>
							<MoreVertIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				),
			},
		],
		[onManageSubmissions, setSelectedStore]
	);

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
			<Stack direction="row" spacing={1.5} alignItems="center" mb={1} flexWrap="wrap" useFlexGap flexShrink={0}>
				{!hideHeader && (
					<>
						<Button
							size="small"
							startIcon={<KeyboardBackspaceRounded />}
							variant="contained"
							onClick={onBack}
							sx={{ flexShrink: 0 }}
						>
							Plans
						</Button>
						<Stack spacing={0.15}>
							<Typography variant="subtitle1" fontWeight={700}>
								Stores
							</Typography>
							<Typography variant="body2" color="text.secondary" fontSize={13}>
								{plan?.name} · {plan?.channel}
								{plan?.brand ? ` · ${plan.brand}` : ""}
							</Typography>
						</Stack>
					</>
				)}
				<Stack
					direction="row"
					spacing={0.75}
					alignItems="center"
					sx={{ ml: { sm: hideHeader ? 0 : "auto" }, width: hideHeader ? "100%" : "auto" }}
					flexWrap="wrap"
					useFlexGap
				>
					<TextField
						size="small"
						placeholder="Store / code / user"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchRoundedIcon sx={{ fontSize: 18, color: grey[500] }} />
								</InputAdornment>
							),
						}}
						sx={{
							width: 240,
							"& .MuiOutlinedInput-root": { height: 32, fontSize: 12, fontWeight: 600 },
						}}
					/>
					<FormControl size="small" sx={{ minWidth: 150 }}>
						<Select
							multiple
							displayEmpty
							value={approvalRoleFilter}
							onChange={handleApprovalRoleFilter}
							renderValue={(selected) => (selected.length ? selected.join(", ") : "Approvals")}
							sx={{ height: 32, fontSize: 12, fontWeight: 600 }}
						>
							<MenuItem disabled value="">
								<em>Approvals</em>
							</MenuItem>
							{STORE_APPROVAL_ROLE_OPTIONS.map((role) => (
								<MenuItem key={role} value={role} dense>
									<Checkbox size="small" checked={approvalRoleFilter.includes(role)} />
									<ListItemText primary={role} primaryTypographyProps={{ fontSize: 13 }} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Chip
						label="All"
						size="small"
						variant={!statusFilter && !approvalRoleFilter.length && !storeSearch ? "filled" : "outlined"}
						onClick={() => {
							setStatusFilter(null);
							setApprovalRoleFilter([]);
							setSearchInput("");
							setStoreSearch("");
							setPaginationModel((prev) => ({ ...prev, page: 0 }));
						}}
						sx={{ height: 26, fontWeight: 600 }}
					/>
					{STORE_APPROVAL_STATUSES.map((status) => (
						<Chip
							key={status}
							label={status}
							size="small"
							color={statusChipColor[status] || "default"}
							variant={statusFilter === status ? "filled" : "outlined"}
							onClick={() => handleStatusFilter(status)}
							sx={{ height: 26, fontWeight: 600 }}
						/>
					))}
					<Chip
						label={rowCount}
						size="small"
						sx={{ height: 26, fontWeight: 700, bgcolor: grey[100], color: grey[700] }}
					/>
				</Stack>
			</Stack>
			<Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
				<DataGrid
					rows={stores}
					columns={columns}
					loading={loading}
					rowCount={rowCount}
					paginationMode="server"
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					pageSizeOptions={[PAGE_SIZE]}
					rowHeight={40}
					columnHeaderHeight={42}
					showCellVerticalBorder
					showColumnVerticalBorder
					disableRowSelectionOnClick
					disableColumnMenu
					onRowClick={handleRowClick}
					getRowClassName={getRowClassName}
					sx={bpHomeGridSx}
				/>
			</Box>
		</Box>
	);
};

export default StoresGrid;
